import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubscription, fetchUsage, upgradePlan, initTopup, initPlanPayment } from '../../../store/slices/usageSlice';
import Modal from '../../../components/Modal';

const PLANS = [
  {
    tier: 'free',
    label: 'Free',
    price: '₦0',
    period: '/month',
    tokens: '100K',
    features: ['100,000 tokens / month', '10 requests / minute', 'Chat, Vision, Embeddings', 'Community support'],
  },
  {
    tier: 'pro',
    label: 'Pro',
    price: '$10',
    period: '/month',
    tokens: '5M',
    features: ['5,000,000 tokens / month', '60 requests / minute', 'All modalities', '18 African languages', 'Email support'],
    highlight: true,
  },
  {
    tier: 'enterprise',
    label: 'Enterprise',
    price: 'Custom',
    period: '',
    tokens: '50M+',
    features: ['50M+ tokens / month', '300 requests / minute', 'Dedicated cluster', 'SLA guarantee', 'Priority support'],
  },
];

const TOPUP_AMOUNTS = [500, 1000, 2500, 5000, 10000];

function fmtTokens(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K';
  return n.toString();
}

export default function Billing() {
  const dispatch = useDispatch();
  const { subscription, subLoading, spend, billing, lastFetched } = useSelector(s => s.usage);

  const [upgradeModal, setUpgradeModal] = useState(false);
  const [topupModal, setTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [topupError, setTopupError] = useState('');

  useEffect(() => {
    const STALE = 5 * 60 * 1000;
    dispatch(fetchSubscription());
    if (!lastFetched || Date.now() - lastFetched > STALE) dispatch(fetchUsage());
  }, []); // eslint-disable-line

  const tier = subscription?.tier || 'free';
  const tokensUsed = subscription?.tokensUsed ?? 0;
  const monthlyLimit = subscription?.monthlyTokenLimit ?? 100_000;
  const creditTokens = subscription?.creditTokens ?? 0;
  const pct = Math.min(100, Math.round((tokensUsed / monthlyLimit) * 100));
  const resetDate = subscription?.resetDate ? new Date(subscription.resetDate).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  const handleUpgrade = async (newTier) => {
    if (newTier === tier) return;
    setUpgrading(true);
    setUpgradeError('');

    if (newTier === 'pro') {
      const res = await dispatch(initPlanPayment({ tier: 'pro' }));
      setUpgrading(false);
      if (initPlanPayment.rejected.match(res)) {
        setUpgradeError(res.payload || 'Payment initialization failed');
      } else if (res.payload?.authorizationUrl) {
        window.location.href = res.payload.authorizationUrl;
      }
      return;
    }

    const res = await dispatch(upgradePlan(newTier));
    setUpgrading(false);
    if (upgradePlan.rejected.match(res)) {
      setUpgradeError(res.payload || 'Upgrade failed');
    } else {
      setUpgradeModal(false);
    }
  };

  const handleTopup = async () => {
    const amt = customAmount ? parseInt(customAmount, 10) : topupAmount;
    if (!amt || amt < 500) { setTopupError('Minimum top-up is ₦500'); return; }
    setToppingUp(true);
    setTopupError('');
    const res = await dispatch(initTopup({ amountNgn: amt }));
    setToppingUp(false);
    if (initTopup.rejected.match(res)) {
      setTopupError(res.payload || 'Top-up failed');
    } else if (res.payload?.authorizationUrl) {
      window.location.href = res.payload.authorizationUrl;
    }
  };

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">ACCOUNT</p>
          <h1 className="dash-page-title">Billing</h1>
          <p className="dash-page-desc">Plan, token usage, and top-up history.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flexShrink: 0, marginTop: 4 }} onClick={() => setUpgradeModal(true)}>
            Change plan
          </button>
          <button className="btn btn-primary" style={{ flexShrink: 0, marginTop: 4 }} onClick={() => setTopupModal(true)}>
            Top up →
          </button>
        </div>
      </div>

      {/* Current plan summary */}
      <div className="billing-top-grid">
        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <div className="dash-card-title">Current plan</div>
              <div className="dash-card-desc">{resetDate ? `Resets ${resetDate}` : 'Monthly reset'}</div>
            </div>
            <span className="badge badge-live">{tier}</span>
          </div>
          <div className="dash-card-body">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: 'var(--fg-mid)' }}>
                <span>{fmtTokens(tokensUsed)} used</span>
                <span>{fmtTokens(monthlyLimit)} limit</span>
              </div>
              <div style={{ height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: pct > 90 ? 'var(--error, #ff5555)' : pct > 70 ? '#f59e0b' : 'var(--accent)',
                  borderRadius: 3,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-faint)', fontFamily: 'var(--font-mono)' }}>
                {pct}% used · {fmtTokens(monthlyLimit - tokensUsed + creditTokens)} remaining (incl. credits)
              </div>
            </div>
            {PLANS.find(p => p.tier === tier)?.features.map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: 'var(--fg-mid)', marginBottom: 6 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="var(--signal, #22c55e)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                {f}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="stat-card">
            <span className="stat-label">Credit tokens</span>
            <div className="stat-num">{subLoading ? '—' : fmtTokens(creditTokens)}</div>
            <div className="stat-delta stat-delta-neutral">from top-ups</div>
          </div>
          <div className="stat-card">
            <span className="stat-label">Cycle spend</span>
            <div className="stat-num">{spend != null ? `$${spend.toFixed(2)}` : '—'}</div>
            <div className="stat-delta stat-delta-neutral">this billing period</div>
          </div>
          <div className="stat-card">
            <span className="stat-label">Tokens used</span>
            <div className="stat-num">{subLoading ? '—' : fmtTokens(tokensUsed)}</div>
            <div className="stat-delta stat-delta-neutral">this month</div>
          </div>
        </div>
      </div>

      {/* Invoice history */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <div className="dash-card-head">
          <div className="dash-card-title">Top-up history</div>
        </div>
        {billing?.invoices?.length > 0 ? (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {billing.invoices.map((h, i) => (
                <tr key={i}>
                  <td className="td-mono">{h.date}</td>
                  <td>{h.desc || h.description}</td>
                  <td className="td-mono">{h.amount}</td>
                  <td><span className="badge badge-live">{h.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dash-empty" style={{ padding: '36px 24px' }}>
            <div className="dash-empty-title" style={{ fontSize: 13 }}>No top-ups yet</div>
            <p className="dash-empty-sub">Your payment history will appear here after your first top-up.</p>
          </div>
        )}
      </div>

      {/* Upgrade modal */}
      {upgradeModal && (
        <Modal onClose={() => setUpgradeModal(false)}>
          <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 className="modal-title">Choose a plan</h2>
              <p className="modal-sub">Switch any time. Changes take effect immediately.</p>
            </div>
            <button className="modal-close" onClick={() => setUpgradeModal(false)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PLANS.map(plan => (
              <div key={plan.tier} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px',
                border: `1px solid ${plan.tier === tier ? 'var(--accent)' : plan.highlight ? 'rgba(139,124,255,0.3)' : 'var(--line)'}`,
                borderRadius: 'var(--r-md)',
                background: plan.tier === tier ? 'rgba(139,124,255,0.05)' : plan.highlight ? 'rgba(139,124,255,0.03)' : 'var(--bg-2)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{plan.label}</span>
                    {plan.highlight && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: 'var(--accent)', color: '#fff', letterSpacing: '0.06em' }}>POPULAR</span>}
                    {plan.tier === tier && <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 20, background: 'rgba(139,124,255,0.15)', color: 'var(--accent)', letterSpacing: '0.06em' }}>CURRENT</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700 }}>{plan.price}</span>
                    <span style={{ fontSize: 11, color: 'var(--fg-dim)' }}>{plan.period} · {plan.tokens} tokens/mo</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px' }}>
                    {plan.features.slice(0, 3).map(f => (
                      <span key={f} style={{ fontSize: 11, color: 'var(--fg-dim)' }}>{f}</span>
                    ))}
                  </div>
                </div>
                <button
                  className={`btn ${plan.tier === tier ? 'btn-ghost' : 'btn-primary'}`}
                  style={{ fontSize: 12, padding: '7px 14px', flexShrink: 0 }}
                  disabled={plan.tier === tier || upgrading || plan.tier === 'enterprise'}
                  onClick={() => handleUpgrade(plan.tier)}
                >
                  {plan.tier === tier ? 'Current' : plan.tier === 'enterprise' ? 'Contact us' : upgrading ? '…' : 'Switch'}
                </button>
              </div>
            ))}
            {upgradeError && <p style={{ color: '#ff8080', fontSize: 12, marginTop: 4 }}>{upgradeError}</p>}
          </div>
        </Modal>
      )}

      {/* Top-up modal */}
      {topupModal && (
        <Modal onClose={() => setTopupModal(false)} maxWidth={440}>
          <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 className="modal-title">Top up tokens</h2>
              <p className="modal-sub">Buy credits that carry over when your monthly quota runs out.</p>
            </div>
            <button className="modal-close" onClick={() => setTopupModal(false)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {TOPUP_AMOUNTS.filter((_, i) => i < 6).map(amt => (
                  <button
                    key={amt}
                    onClick={() => { setTopupAmount(amt); setCustomAmount(''); }}
                    style={{
                      padding: '10px 8px',
                      borderRadius: 'var(--r-sm)',
                      border: `1px solid ${topupAmount === amt && !customAmount ? 'var(--accent)' : 'var(--line)'}`,
                      background: topupAmount === amt && !customAmount ? 'rgba(139,124,255,0.1)' : 'var(--bg-2)',
                      color: 'var(--fg)',
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    ₦{amt.toLocaleString()}
                  </button>
                ))}
              </div>
              <div className="field">
                <label>Custom amount (₦)</label>
                <input
                  type="number"
                  placeholder="e.g. 7500"
                  value={customAmount}
                  min={500}
                  onChange={e => { setCustomAmount(e.target.value); setTopupAmount(0); }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>
                ₦{(customAmount || topupAmount).toLocaleString()} → ~{fmtTokens(Math.floor(((customAmount || topupAmount) / 5000) * 1_000_000))} tokens
              </div>
              {topupError && <p style={{ color: '#ff8080', fontSize: 12 }}>{topupError}</p>}
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px 0' }} onClick={handleTopup} disabled={toppingUp}>
                {toppingUp ? 'Processing…' : `Pay ₦${(customAmount || topupAmount).toLocaleString()} via Paystack`}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}
